import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH27_QUESTIONS } from "../data/neetPhysicsCh27Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 27: Atoms (NCERT Class 12 Physics).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Rutherford's Atomic Model", "Bohr's Model of the Atom", "Energy Levels & the Hydrogen Spectrum", "Excitation & Ionization"];

const SECTION_COLORS = {
  "Rutherford's Atomic Model":               "#0891b2",
  "Bohr's Model of the Atom":                "#7c3aed",
  "Energy Levels & the Hydrogen Spectrum":   "#dc2626",
  "Excitation & Ionization":                 "#16a34a",
};

const CONCEPTS = [
  // ── Rutherford's Atomic Model ───────────────────────────────────────────────
  { section: "Rutherford's Atomic Model", title: "Alpha-particle scattering experiment — setup and procedure",
    explanation: "Rutherford, Geiger and Marsden directed a narrow beam of fast alpha particles (~5.5 MeV, from a radioactive source) at a thin gold foil (a few micrometres thick, chosen since gold is highly malleable) inside an evacuated chamber. A circular fluorescent zinc sulphide (ZnS) screen surrounded the foil; tiny flashes of light (scintillations) produced where alpha particles struck the screen were counted with a movable microscope at different scattering angles, mapping how many alpha particles were deflected through each angle.",
    example: `Typical setup:
Alpha source : polonium-210 (K.E. ~ 5.5 MeV)
Foil         : gold, thickness ~ 2 x 10^-7 m (very thin)
Detector     : ZnS scintillation screen + microscope, movable in angle
Recorded     : number of scintillations vs. scattering angle theta` },
  { section: "Rutherford's Atomic Model", title: "Observations of the alpha-scattering experiment",
    explanation: "Most alpha particles (~99.9%) passed straight through the foil with little or no deflection, showing that most of the atom is empty space. A small fraction were deflected through moderate angles, and roughly 1 in 8000 alpha particles bounced back through angles greater than 90 degrees (some almost 180 degrees) -- a result so unexpected that Rutherford compared it to a bullet bouncing off tissue paper. Such large-angle scattering could only happen if the positive charge and nearly all the mass of the atom were concentrated in a tiny, dense central core.",
    example: `~99.9% of alpha particles : deflection ~0 degrees (undeflected)
Small fraction            : deflected at moderate angles
~1 in 8000                : deflected at angle > 90 degrees (back-scattered)

=> implies a tiny, massive, positively charged nucleus` },
  { section: "Rutherford's Atomic Model", title: "Rutherford's nuclear model of the atom",
    explanation: "Based on the scattering data, Rutherford proposed that (i) the entire positive charge and almost all the mass of an atom are concentrated in a tiny central core called the nucleus, (ii) the nucleus's size (~10^-15 to 10^-14 m) is about 10,000 times smaller than the size of the atom (~10^-10 m), and (iii) electrons revolve around the nucleus in circular orbits, held by electrostatic (Coulomb) attraction -- much like planets orbit the Sun under gravity. In a head-on collision, the distance of closest approach (found by equating the alpha particle's kinetic energy to the electrostatic potential energy at the point where it momentarily stops) gives an estimate of the nuclear size.",
    example: `Head-on collision: K.E. of alpha = (1/4*pi*eps0) (2*Z*e^2) / r0

For gold (Z = 79), alpha K.E. = 5.5 MeV = 8.8 x 10^-13 J:
r0 = (9x10^9)(2)(79)(1.6x10^-19)^2 / (8.8x10^-13)
r0 ~ 4.1 x 10^-14 m   (order of 10^-14 m -- confirms a tiny nucleus)` },
  { section: "Rutherford's Atomic Model", title: "Limitations of Rutherford's atomic model",
    explanation: "Classical electromagnetic theory predicts that an accelerating (orbiting) charge must continuously radiate energy as electromagnetic waves. An electron revolving around the nucleus is undergoing centripetal (accelerated) motion, so classically it should continuously lose energy, spiral inward, and collapse into the nucleus in a fraction of a second (~10^-8 s) -- meaning atoms would be highly unstable, contradicting the observed stability of matter. This continuous energy loss would also predict a continuous emission spectrum, but atoms are observed to emit sharp, discrete LINE spectra -- a fact Rutherford's model could not explain at all.",
    example: `Classical prediction: an orbiting (accelerating) electron radiates
  energy continuously
=> electron should spiral into the nucleus in ~10^-8 s (atom collapses)
   -- NOT observed; atoms are stable
=> also predicts a continuous spectrum, but real atoms show discrete
   LINE spectra -- also NOT explained
Both contradictions => a new (quantum) theory was needed` },

  // ── Bohr's Model of the Atom ────────────────────────────────────────────────
  { section: "Bohr's Model of the Atom", title: "Bohr's postulates of the atomic model",
    explanation: "(1) Electrons revolve around the nucleus only in certain allowed circular orbits called STATIONARY ORBITS, without radiating energy despite being accelerated -- classical electromagnetic theory simply does not apply inside these orbits. (2) These stationary orbits are ones in which the electron's angular momentum is quantized in integral multiples of h/2*pi: L = mvr = n(h/2*pi), where n = 1, 2, 3, ... is the principal quantum number. (3) Radiation (a photon) is emitted or absorbed only when an electron JUMPS from one stationary orbit to another; the photon's energy equals the energy difference between the two orbits: h*nu = E_higher - E_lower.",
    example: `Postulate 2 (quantization):  L = mvr = n(h/2*pi),  n = 1,2,3,...
Postulate 3 (transition):    h*nu = E2 - E1   (photon emitted, E2 > E1)

n=1: L = h/2*pi = 1.055 x 10^-34 J.s  (smallest allowed angular momentum)` },
  { section: "Bohr's Model of the Atom", title: "Bohr radius — derivation of the radius formula",
    explanation: "Combine the quantization condition mvr = n(h/2*pi) with Newton's second law for circular motion, where the Coulomb attraction between the nucleus (+Ze) and the electron (-e) supplies the centripetal force: (1/4*pi*eps0)(Z*e^2/r^2) = m*v^2/r. Eliminating v between the two equations gives the radius of the nth orbit: r_n = (eps0 * n^2 * h^2) / (pi * m * Z * e^2) = 0.529 * (n^2/Z) Angstrom. The n=1, Z=1 case defines the BOHR RADIUS, a0 = 0.529 Angstrom = 5.29 x 10^-11 m -- the radius of hydrogen's smallest (ground-state) orbit.",
    example: `Coulomb force = centripetal force:  k*Z*e^2/r^2 = m*v^2/r      ... (i)
Quantization:                       v = n*h / (2*pi*m*r)          ... (ii)

Substitute (ii) into (i):  r_n = (n^2 h^2 eps0)/(pi m Z e^2) = 0.529(n^2/Z) A
For hydrogen (Z=1), n=1:   r_1 = a0 = 0.529 Angstrom = 5.29 x 10^-11 m` },
  { section: "Bohr's Model of the Atom", title: "Radius of the nth orbit in a hydrogen atom",
    explanation: "For hydrogen (Z=1), the radius formula simplifies to r_n = 0.529 * n^2 Angstrom, so successive orbits grow as the SQUARE of the principal quantum number -- orbits get rapidly farther apart as n increases. For a general one-electron (hydrogen-like) ion of atomic number Z, r_n = 0.529 * (n^2/Z) Angstrom -- a higher nuclear charge pulls the electron into a smaller orbit for the same n.",
    example: `r_n = 0.529 x n^2  Angstrom   (hydrogen, Z=1)

n=1: r1 = 0.529 x 1  = 0.529  Angstrom
n=2: r2 = 0.529 x 4  = 2.116  Angstrom
n=3: r3 = 0.529 x 9  = 4.761  Angstrom
n=4: r4 = 0.529 x 16 = 8.464  Angstrom

For He+ (Z=2), n=1: r1 = 0.529 x (1/2) = 0.2645 Angstrom` },
  { section: "Bohr's Model of the Atom", title: "Velocity of an electron in a Bohr orbit",
    explanation: "From v = n*h/(2*pi*m*r_n), substituting r_n gives v_n = Z*e^2/(2*eps0*n*h) = (Z/n) x 2.18 x 10^6 m/s. So orbital speed DECREASES as n increases (an electron in an outer, higher-energy orbit moves more slowly) and increases with nuclear charge Z. For hydrogen's ground state, v1 ~ 2.18 x 10^6 m/s ~ c/137 -- small enough that non-relativistic mechanics remains a reasonable approximation.",
    example: `v_n = (Z/n) x 2.18 x 10^6 m/s     (hydrogen-like atom)

Hydrogen (Z=1):
v1 = 2.18 x 10^6 m/s   (n=1, fastest, innermost orbit)
v2 = 1.09 x 10^6 m/s   (n=2, half as fast as v1)
v3 = 0.727 x 10^6 m/s  (n=3)

v1 / c ~ (2.18x10^6)/(3x10^8) ~ 1/137` },

  // ── Energy Levels & the Hydrogen Spectrum ───────────────────────────────────
  { section: "Energy Levels & the Hydrogen Spectrum", title: "Energy of an electron in the nth Bohr orbit",
    explanation: "The electron's total energy is the sum of its kinetic energy (positive, from orbital motion) and electrostatic potential energy (negative, since it is bound to the nucleus): by the virial theorem for a Coulomb-bound circular orbit, KE = -PE/2, so E_n = KE + PE = PE/2. Substituting r_n gives E_n = -13.6 (Z^2/n^2) eV for a hydrogen-like atom. The negative sign shows the electron is BOUND; energy must be supplied to remove it (n -> infinity gives E=0, a free electron at rest).",
    example: `E_n = -13.6 x (Z^2/n^2)  eV     (hydrogen-like atom)

Hydrogen (Z=1):
E1 = -13.6 eV      (ground state)
E2 = -13.6/4  = -3.4  eV
E3 = -13.6/9  = -1.51 eV
E4 = -13.6/16 = -0.85 eV
E_infinity = 0     (electron just free, at rest)` },
  { section: "Energy Levels & the Hydrogen Spectrum", title: "Energy level diagram of the hydrogen atom",
    explanation: "Plotting E_n on a vertical energy axis gives the hydrogen atom's energy level diagram: levels are most widely spaced near the ground state and crowd together as n -> infinity, approaching the zero-energy ionization limit. Any transition of the electron between two levels corresponds to a vertical line on this diagram, with the photon energy equal to the gap between the levels -- downward transitions emit a photon, upward transitions (excitation) require absorbing one.",
    example: `n=1  ---------------- -13.6 eV   (ground state)
n=2  ---------------- -3.4  eV
n=3  ---------------- -1.51 eV
n=4  ---------------- -0.85 eV
n=5  ---------------- -0.54 eV
n=inf---------------- 0     eV   (ionization limit)

Energy released, n=2 -> n=1: Delta-E = -3.4 - (-13.6) = 10.2 eV (photon emitted)` },
  { section: "Energy Levels & the Hydrogen Spectrum", title: "Rydberg formula for the hydrogen spectrum",
    explanation: "The wavelength of a photon emitted when an electron falls from a higher orbit n2 to a lower orbit n1 (n2 > n1) in a hydrogen-like atom is given by the Rydberg formula: 1/lambda = R * Z^2 * (1/n1^2 - 1/n2^2), where R = 1.097 x 10^7 m^-1 is the Rydberg constant. Different choices of the lower level n1 group the spectral lines into named SERIES (Lyman, Balmer, Paschen, Brackett, Pfund); within each series, n2 = n1+1, n1+2, ... gives successive lines, with n2 -> infinity giving that series' SERIES LIMIT (shortest wavelength / highest energy line of the series).",
    example: `1/lambda = R * Z^2 * (1/n1^2 - 1/n2^2),   R = 1.097 x 10^7 m^-1

Series limit (n2 -> infinity) for a series starting at n1:
1/lambda_limit = R * Z^2 / n1^2

For hydrogen (Z=1), n1=1 (Lyman limit):
1/lambda = 1.097 x 10^7 (1)  ->  lambda = 91.2 nm` },
  { section: "Energy Levels & the Hydrogen Spectrum", title: "The Lyman series (ultraviolet)",
    explanation: "The Lyman series arises from transitions in hydrogen ending at n1=1 (the ground state), from any n2 = 2, 3, 4, .... Because the energy gap to the ground state is largest, all Lyman lines lie in the ULTRAVIOLET region. It is the only series whose lower level is the ground state, so it involves the LARGEST photon energies (shortest wavelengths) among hydrogen's series.",
    example: `1/lambda = R(1/1^2 - 1/n2^2),  n2 = 2,3,4,...

First line (n2=2, "Lyman-alpha"):
1/lambda = 1.097x10^7 (1 - 1/4) = 1.097x10^7 x 0.75 = 8.228x10^6 m^-1
lambda = 121.6 nm  (UV)

Series limit (n2 -> infinity):
1/lambda = 1.097x10^7  ->  lambda = 91.2 nm  (UV)` },
  { section: "Energy Levels & the Hydrogen Spectrum", title: "The Balmer series (visible)",
    explanation: "The Balmer series arises from transitions ending at n1=2, from n2 = 3, 4, 5, .... This is the only hydrogen series with lines falling substantially in the VISIBLE region, which is how it was discovered (and named) before Bohr's theory even existed; its first four lines are the familiar red (H-alpha), blue-green (H-beta), blue (H-gamma), and violet (H-delta) lines of the hydrogen spectrum, while higher members and the series limit lie in the near ultraviolet.",
    example: `1/lambda = R(1/2^2 - 1/n2^2),  n2 = 3,4,5,...

First line (n2=3, "H-alpha", red):
1/lambda = 1.097x10^7 (1/4 - 1/9) = 1.097x10^7 x 0.1389 = 1.524x10^6 m^-1
lambda = 656.3 nm   (red, visible)

Series limit (n2 -> infinity):
1/lambda = R/4 = 2.7425x10^6 m^-1  ->  lambda = 364.6 nm (near-UV)` },
  { section: "Energy Levels & the Hydrogen Spectrum", title: "The Paschen, Brackett and Pfund series (infrared)",
    explanation: "Transitions ending at n1=3 (Paschen), n1=4 (Brackett), and n1=5 (Pfund) all produce photons of comparatively low energy, so all three series lie in the INFRARED region, shifting to longer wavelengths (and more closely spaced lines) as n1 increases.",
    example: `Paschen (n1=3, n2=4,5,...): first line (n2=4):
1/lambda = R(1/9-1/16) = 1.097x10^7 x 0.04861 = 5.334x10^5 m^-1 -> lambda = 1875 nm

Brackett (n1=4, n2=5,6,...): first line (n2=5):
1/lambda = R(1/16-1/25) = 1.097x10^7 x 0.0225 = 2.468x10^5 m^-1 -> lambda = 4051 nm

Pfund (n1=5, n2=6,7,...): first line (n2=6):
1/lambda = R(1/25-1/36) = 1.097x10^7 x 0.01222 = 1.341x10^5 m^-1 -> lambda = 7458 nm

All three series lie in the infrared region` },
  { section: "Energy Levels & the Hydrogen Spectrum", title: "Line spectra as evidence of discrete energy levels",
    explanation: "Because atoms emit or absorb light only at specific, sharp wavelengths (discrete bright lines on a dark background in emission, or dark lines on a continuous background in absorption) rather than a continuous range, this is direct experimental proof that an atom's electrons occupy discrete, QUANTIZED energy levels rather than a continuum -- exactly as Bohr's model demanded. Each element has its own unique line spectrum, a 'fingerprint' used to identify elements (e.g. in stars, via spectroscopy).",
    example: `Continuous spectrum : all wavelengths present (e.g. an incandescent filament)
Line (emission) spectrum : only specific discrete wavelengths present
  -- e.g. hydrogen: 656.3 nm, 486.1 nm, 434.0 nm, 410.2 nm (Balmer lines) ...

Each line <-> one specific transition between two quantized energy levels` },

  // ── Excitation & Ionization ──────────────────────────────────────────────────
  { section: "Excitation & Ionization", title: "Excitation energy and excitation potential",
    explanation: "The excitation energy of a given level is the energy needed to raise an electron from the ground state (n=1) up to that level: Delta-E = E_n - E_1. The FIRST excitation energy (ground state -> n=2) of hydrogen is 10.2 eV. The excitation POTENTIAL is this energy expressed in volts (the accelerating voltage needed for a bombarding electron to supply exactly this energy) -- numerically equal to the excitation energy in eV, since 1 eV is the energy gained by an electron accelerated through 1 volt.",
    example: `First excitation energy (n=1 -> n=2):
Delta-E = E2 - E1 = -3.4 - (-13.6) = 10.2 eV
=> first excitation potential = 10.2 V

Second excitation energy (n=1 -> n=3):
Delta-E = E3 - E1 = -1.51 - (-13.6) = 12.09 eV
=> second excitation potential = 12.09 V` },
  { section: "Excitation & Ionization", title: "Ionization energy and ionization potential",
    explanation: "Ionization energy is the minimum energy needed to remove the electron completely from the atom, i.e. to take it from its initial level to n=infinity (E=0): for hydrogen's ground state, this is 13.6 eV, since E_ionization = E_infinity - E_1 = 0 - (-13.6) = 13.6 eV. The ionization POTENTIAL (13.6 V for ground-state hydrogen) is this energy expressed in volts. If the atom is already in an excited state, less energy is needed -- e.g. from n=2, the ionization energy is only 3.4 eV.",
    example: `Ionization energy from ground state (n=1):
E_ionization = E_infinity - E_1 = 0 - (-13.6) = 13.6 eV
=> ionization potential of hydrogen = 13.6 V

Ionization energy from first excited state (n=2):
E_ionization = 0 - (-3.4) = 3.4 eV   (much less energy needed)` },
  { section: "Excitation & Ionization", title: "Number of spectral lines emitted during de-excitation",
    explanation: "If an electron sits in an excited state with principal quantum number n, it can cascade down to the ground state through many possible paths, and the total number of distinct spectral lines that can be emitted (across all atoms in a sample, each possibly taking a different path) is the number of ways to choose 2 levels out of n available levels: N = n(n-1)/2. This counts every possible pairwise downward transition among the n occupied levels.",
    example: `N = n(n-1)/2

Electron initially in n=2: N = 2x1/2 = 1  line
Electron initially in n=3: N = 3x2/2 = 3  lines
Electron initially in n=4: N = 4x3/2 = 6  lines
Electron initially in n=5: N = 5x4/2 = 10 lines` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh27Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh27Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh27Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH27_QUESTIONS : NEET_PHYSICS_CH27_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 27</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Atoms</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12, Ch. 12 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH27_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH27_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
